/**
 * Chrome/Edge Translate (and page-translating extensions) rewrite text nodes React
 * owns, wrapping them in <font> elements. React's commit phase then calls
 * removeChild/insertBefore against the parent it remembers, the node has been
 * re-parented underneath, and the DOM throws:
 *
 *   Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.
 *   Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node.
 *
 * That throw happens mid-commit, so it takes the whole render down rather than one
 * component. Measured on prod Diagnostics.Logs: 1,589 + 310 events across ~350
 * accounts in 90 days — the largest client error in the app by 3x, and zero
 * support tickets, because the users affected are the ones reading CM in another
 * language and they simply leave.
 *
 * ErrorBoundary has recovered from this since 2026-03 by re-rendering, but
 * re-rendering into the same translated DOM hits the same throw, so it only buys
 * three strikes. This prevents the throw instead.
 *
 * The guards intervene ONLY in the exact state that would otherwise throw, so a
 * genuine reconciliation bug still surfaces normally.
 */

const REPORT_DELAY_MS = 15000;
const MAX_WRAPPER_DEPTH = 6;

let translatedHits = 0; // rescued under a translator wrapper — expected, not our bug
let foreignHits = 0; // rescued with NO wrapper — one of ours, and otherwise invisible
let reportScheduled = false;

/**
 * A translator leaves its signature in the DOM: the rescued node now sits under a
 * <font> somewhere between its real parent and the parent React expected.
 *
 * 🔴 Counting the two cases separately is what stops this guard from hiding our own
 * bugs. Before it existed, one of our reconciliation faults surfaced as a removeChild
 * error in Diagnostics.Logs. Now the guard swallows that throw, so without this split
 * a real bug and a Chrome artifact would look identical and both would read as zero.
 */
const underTranslatorWrapper = (node: Node, expectedParent: Node): boolean => {
  let cur: Node | null = node.parentNode;
  for (let i = 0; cur && cur !== expectedParent && i < MAX_WRAPPER_DEPTH; i++) {
    if ((cur as Element).tagName === "FONT") return true;
    cur = cur.parentNode;
  }
  return false;
};

const record = (translated: boolean) => {
  if (translated) translatedHits += 1;
  else foreignHits += 1;

  if (reportScheduled) return;
  reportScheduled = true;

  // One trace per affected page load, not one per suppression: this replaces the
  // 5-15 error rows a translated session used to write with a single row, and it
  // is the denominator that tells us whether the guard is firing at all. Delayed
  // rather than sent on pagehide, so the batched event queue actually flushes.
  // `other=` above zero is the signal worth alerting on — that is our code.
  window.setTimeout(() => {
    void import("./features/analytics/analytics")
      .then(({ trackEvent }) =>
        trackEvent({
          type: "trace",
          category: "app",
          action: "translation-dom-guard",
          label: `font=${translatedHits} other=${foreignHits}`,
          value: translatedHits + foreignHits,
          internal: true,
        })
      )
      .catch(() => {
        // The reporter must never break the page it is reporting on, and the module
        // it loads is itself reachable only through the import path this room fixed.
      });
  }, REPORT_DELAY_MS);
};

/** Suppressions so far in this page load, for console inspection. */
export const getSuppressedCount = () => ({
  translated: translatedHits,
  foreign: foreignHits,
});

export const installTranslationGuard = () => {
  // 🔴 This runs before createRoot. An uncaught throw here renders NOTHING, for
  // everyone — a far worse outcome than the crash it prevents. Degrade to unguarded.
  try {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
      if (child.parentNode !== this) {
        const translated = underTranslatorWrapper(child, this);
        // Remove it from where it actually lives — that is what React meant to do.
        if (child.parentNode) {
          originalRemoveChild.call(child.parentNode, child);
        }
        record(translated);
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(
      this: Node,
      newNode: T,
      referenceNode: Node | null
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        // Append rather than throw. Order can be wrong until the next full render;
        // a wrong order is recoverable, a dead page is not.
        record(underTranslatorWrapper(referenceNode, this));
        return originalInsertBefore.call(this, newNode, null) as T;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
  } catch {
    // Prototypes not writable (hardened runtime, an extension got there first).
    // The app boots and behaves exactly as it did before this guard existed.
  }
};
